import DraggableWidget from "./DraggableWidget";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ArrowUpDown, Eye } from "lucide-react";

interface Player {
  id: string;
  name: string;
  position: string;
  team: string;
  minutes: number;
  points: number;
  goals?: number;
  assists?: number;
  shots?: number;
  // CSGO stats
  kills?: number;
  deaths?: number;
  adr?: number;
  status: 'playing' | 'finished' | 'bench';
}

interface PlayerTableWidgetProps {
  id: string;
  players?: Player[];
  sport?: 'soccer' | 'csgo';
  sortBy?: string;
  onSort?: (field: string) => void;
  onPlayerClick?: (player: Player) => void;
  onDragStart?: (id: string) => void;
  onDragEnd?: () => void;
  isDragging?: boolean;
}

const PlayerTableWidget = ({
  id,
  players = [],
  sport = 'soccer',
  sortBy,
  onSort,
  onPlayerClick,
  onDragStart,
  onDragEnd,
  isDragging
}: PlayerTableWidgetProps) => {
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'playing':
        return <Badge className="bg-green-500 text-white">Live</Badge>;
      case 'finished':
        return <Badge variant="secondary">Done</Badge>;
      case 'bench':
        return <Badge variant="outline">Bench</Badge>;
      default:
        return null;
    }
  };

  const soccerColumns = [
    { key: 'name', label: 'Player' },
    { key: 'position', label: 'Pos' },
    { key: 'minutes', label: 'Min' },
    { key: 'points', label: 'Pts' },
    { key: 'goals', label: 'G' },
    { key: 'assists', label: 'A' },
    { key: 'shots', label: 'SOT' },
  ];

  const csgoColumns = [
    { key: 'name', label: 'Player' },
    { key: 'team', label: 'Team' },
    { key: 'points', label: 'Pts' },
    { key: 'kills', label: 'K' },
    { key: 'deaths', label: 'D' },
    { key: 'adr', label: 'ADR' },
  ];

  const columns = sport === 'soccer' ? soccerColumns : csgoColumns;

  return (
    <DraggableWidget
      id={id}
      title={`Player Breakdown (${sport.toUpperCase()})`}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      isDragging={isDragging}
    >
      <div className="max-h-80 overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((column) => (
                <TableHead key={column.key} className="text-xs">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-auto p-0 font-semibold"
                    onClick={() => onSort?.(column.key)}
                  >
                    {column.label}
                    {sortBy === column.key && <ArrowUpDown className="ml-1 h-3 w-3" />}
                  </Button>
                </TableHead>
              ))}
              <TableHead className="text-xs">Status</TableHead>
              <TableHead className="text-xs w-16"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {players.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + 2} className="text-center text-muted-foreground py-8">
                  No player data available
                </TableCell>
              </TableRow>
            ) : (
              players.map((player) => (
                <TableRow key={player.id} className="hover:bg-muted/50">
                  <TableCell className="font-medium text-sm">{player.name}</TableCell>
                  
                  {sport === 'soccer' ? (
                    <>
                      <TableCell className="text-xs">{player.position}</TableCell>
                      <TableCell className="font-mono text-xs">{player.minutes}</TableCell>
                      <TableCell className="font-mono font-semibold text-xs">{player.points.toFixed(1)}</TableCell>
                      <TableCell className="font-mono text-xs">{player.goals || 0}</TableCell>
                      <TableCell className="font-mono text-xs">{player.assists || 0}</TableCell>
                      <TableCell className="font-mono text-xs">{player.shots || 0}</TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell className="text-xs">{player.team}</TableCell>
                      <TableCell className="font-mono font-semibold text-xs">{player.points.toFixed(1)}</TableCell>
                      <TableCell className="font-mono text-xs">{player.kills || 0}</TableCell>
                      <TableCell className="font-mono text-xs">{player.deaths || 0}</TableCell>
                      <TableCell className="font-mono text-xs">{player.adr?.toFixed(1) || '0.0'}</TableCell>
                    </>
                  )}
                  
                  <TableCell>
                    {getStatusBadge(player.status)}
                  </TableCell>
                  
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onPlayerClick?.(player)}
                      className="h-6 w-6 p-0"
                    >
                      <Eye className="h-3 w-3" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </DraggableWidget>
  );
};

export default PlayerTableWidget;